import type { FunctionalComponent } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { Header } from '@/components/header.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { InterviewPopup } from '@/components/webzine/interview-popup.tsx';
import { QrCode } from '@/components/webzine/qr-code.tsx';
import { StreamingLinks } from '@/components/webzine/streaming-links.tsx';
import { WebzineFooter } from '@/components/webzine/webzine-footer.tsx';
import { useAnchorScroll, useScrollHeader } from '@/hooks/use-scroll-header.ts';
import { flattenTitle, issueHref, type WebzineIssue } from '@/types/webzine.ts';
import { formatIssueDate } from '@/types/webzine.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import { formatPrice } from '@/lib/utils.ts';

/** 유튜브 재생 아이콘 (시안 `_index.html` 인라인 SVG) */
/**
 * 음악 미션의 재생 표시.
 *
 * 유튜브 로고를 쓰면 안 된다 — 눌러도 유튜브로 가지 않고 우리 플레이어에서
 * mp3 가 나온다. 중립적인 재생 버튼을 쓴다.
 */
const MusicPlayIcon: FunctionalComponent = () => (
  <span class="teaser-play-icon teaser-play-icon--music">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="20" fill="rgba(0,0,0,0.55)" />
      <path d="M16 12.5L28 20L16 27.5V12.5Z" fill="#fff" />
    </svg>
  </span>
);

const PlayIcon: FunctionalComponent = () => (
  <span class="teaser-play-icon">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28.57 20">
      <g>
        <path
          d="M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 2.24288e-07 14.285 0 14.285 0C14.285 0 5.35042 2.24288e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C2.24288e-07 5.35042 0 10 0 10C0 10 2.24288e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z"
          fill="#FF0000"
        />
        <path d="M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z" fill="white" />
      </g>
    </svg>
  </span>
);

/** `\n` 이 들어간 문자열을 `<br>` 로 나눠 그린다. */
const Lines: FunctionalComponent<{ text: string }> = ({ text }) => (
  <>
    {text.split('\n').map((line, i) => (
      <>
        {i > 0 && <br />}
        {line}
      </>
    ))}
  </>
);

interface Props {
  issue: WebzineIssue;
  /** 'Back Issues' 에 깔 다른 호들 */
  backIssues: WebzineIssue[];
  /** 최신 호의 slug. 그 호로 가는 링크는 홈(`/`)으로 보낸다. */
  latestSlug: string | null;
  /** 탭바 활성 표시용 경로. 홈은 '/', 지난 호는 '/webzine'. */
  tabPath: string;
}

/**
 * 웹진 한 호 전체 화면.
 *
 * 홈(`/`)과 지난 호(`/webzine/:vol`)가 같은 레이아웃이라 한 컴포넌트를 공유한다.
 * 시안 `_index.html`(VOL.03) 과 `vol1.html` / `vol2.html` 의 차이는 인터뷰와
 * 이벤트 배너 유무뿐이고, 둘 다 데이터에서 null 로 표현된다.
 *
 * 시안의 스토리 원형 장식(`.circle-wrap`)은 옮기지 않았다. 시안 SCSS 에서 해당
 * 스타일이 통째로 주석 처리돼 있어 마크업만 있고 아무것도 그려지지 않는다.
 */
export const IssueView: FunctionalComponent<Props> = ({
  issue,
  backIssues,
  latestSlug,
  tabPath,
}) => {
  const dispatch = useAppDispatch();

  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [interviewOpen, setInterviewOpen] = useState(false);

  useScrollHeader(sectionRef, headerRef);
  useAnchorScroll(sectionRef, headerRef);

  const coverTitle = flattenTitle(issue.title);

  /** 아직 이식되지 않았거나 대상이 없는 링크 */
  function notReady(e: MouseEvent, what: string) {
    e.preventDefault();
    void dispatch(askAlert(`${what} 화면은 준비 중입니다.`));
  }

  function goInternal(e: MouseEvent, href: string) {
    e.preventDefault();
    route(href);
  }

  return (
    <div class="page page--hero">
      <Header elementRef={headerRef} />

      {/* 탭바만 고정하고 이 안쪽만 스크롤한다 */}
      <div class="section" ref={sectionRef}>
        <div class="contents">
          <main class="webzine">
            {/* 커버 */}
            <section class="wz-hero wz-hero--cover">
              <div class="hero-cover-masthead">
                <div class="hero-cover-logo">
                  <img src="/images/logo_w.svg" alt="Urnplay" />
                  <span class="hero-cover-vol">{issue.vol}</span>
                </div>
              </div>
              <div class="hero-cover-photo-wrap">
                <SafeImage class="hero-cover-photo" src={issue.cover} alt={coverTitle} />
              </div>
              <div class="hero-cover-scrim" />
              <div class="hero-content">
                {/*
                  QR 은 저장된 이미지가 아니라 링크로 그린다. 다른 기기로 찍으면
                  그 주소가 열린다. 미션이 있는 호에서는 같은 기기에서 눌러 미션
                  영역으로 내려갈 수도 있게 앵커를 씌운다.
                */}
                {issue.mission ? (
                  <a href="#lyrics" class="hero-cover-qr" aria-label="미션으로 이동">
                    <QrCode value={issue.qr} label="이 호 주소 QR" />
                  </a>
                ) : (
                  <span class="hero-cover-qr">
                    <QrCode value={issue.qr} label="이 호 주소 QR" />
                  </span>
                )}
                <div class="hero-cover-text">
                  <h1 class="hero-title">
                    <Lines text={issue.title} />
                  </h1>
                  <p class="hero-artist">
                    <span>artist</span>
                    {issue.artist}
                  </p>
                </div>
              </div>
            </section>

            {/* SONG STORY */}
            <section class="wz-story" id="story">
              <div class="story-inner">
                <div class="section-inner">
                  <h2 class="story-heading">
                    <span class="story-heading-text">
                      <Lines text={issue.story.heading} />
                    </span>
                  </h2>
                  <div class="story-body">
                    {issue.story.body.map((paragraph, i) => (
                      <>
                        {i > 0 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                        {paragraph}
                      </>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* 아티스트 인터뷰 */}
            {issue.interview && (
              <section class="wz-interview" id="artist">
                <div class="section-inner">
                  <div class="interview-top">
                    <h2 class="section-heading">INTERVIEW</h2>
                  </div>
                  <div class="interview-visual">
                    <SafeImage
                      class="interview-photo"
                      src={issue.interview.photo}
                      alt={issue.interview.name}
                    />
                  </div>
                  <div class="interview-bottom">
                    <p class="interview-intro">{issue.interview.intro}</p>
                    <p class="interview-body">
                      {issue.interview.summary.map((line, i) => (
                        <>
                          {i > 0 && <br />}
                          {line}
                        </>
                      ))}
                    </p>
                  </div>
                </div>
                <a
                  href="#artist"
                  class="interview-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setInterviewOpen(true);
                  }}
                >
                  인터뷰 전체보기
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1"
                    stroke-linecap="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </a>
              </section>
            )}

            {/*
              미션 (재생 보상) — 연결된 미션이 있을 때만.

              ★ 형식에 따라 모양이 다르다. 음악은 앨범 카드(정사각)에 중립적인 재생
                버튼, 영상은 16:9 썸네일에 유튜브 표시다. 눌렀을 때 열리는 것이
                다르므로 보이는 것도 달라야 한다.
            */}
            {issue.mission && (
              <section class="wz-dark-group">
                <div class="wz-teaser wz-teaser--light" id="lyrics">
                  <div class="section-inner">
                    <h2 class="section-heading teaser-mission-heading">
                      {issue.mission.type === 'M' ? '지금 듣고' : '지금 보고'}{' '}
                      <img src="/images/point.png" alt="" />
                      {formatPrice(issue.mission.reward)}P 받기
                    </h2>
                    <div
                      class={
                        issue.mission.type === 'M' ? 'teaser-video teaser-video--music' : 'teaser-video'
                      }
                    >
                      <a
                        href={`/play/${issue.mission.campaignSeq}`}
                        class="reel-thumb"
                        onClick={(e) => goInternal(e, `/play/${issue.mission!.campaignSeq}`)}
                      >
                        <SafeImage
                          src={issue.mission.thumbnail}
                          alt={issue.mission.title || coverTitle}
                        />
                        <span class="reward-chip">
                          <img src="/images/point.png" alt="" />
                          {formatPrice(issue.mission.reward)}
                        </span>
                        {issue.mission.type === 'M' ? <MusicPlayIcon /> : <PlayIcon />}
                      </a>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* 스트리밍 — 캠페인에 등록된 링크가 있을 때만 */}
            {issue.streaming.length > 0 && (
              <section class="wz-streaming" id="listen">
                <div class="section-inner">
                  <StreamingLinks services={issue.streaming} />
                </div>
              </section>
            )}

            {/* 이벤트 배너 */}
            {issue.eventBanner && (
              <section class="wz-event-shortcut">
                <a
                  href={issue.eventBanner.href ?? '#'}
                  class="event-shortcut-banner"
                  onClick={(e) =>
                    issue.eventBanner?.href
                      ? goInternal(e, issue.eventBanner.href)
                      : notReady(e, '이벤트')
                  }
                >
                  <SafeImage src={issue.eventBanner.image} alt="이벤트" />
                </a>
              </section>
            )}

            {/* 지난 호 */}
            {backIssues.length > 0 && (
              <section class="wz-more">
                <div class="section-inner">
                  <div class="wz-more-header">
                    <h2 class="section-heading">Back Issues</h2>
                  </div>
                  <div class="post-grid">
                    {backIssues.map((back) => (
                      <a
                        key={back.slug}
                        href={issueHref(back, latestSlug)}
                        class="post-card"
                        onClick={(e) => goInternal(e, issueHref(back, latestSlug))}
                      >
                        <span class="post-thumb">
                          <SafeImage src={back.card.thumbnail} alt={back.card.title} />
                        </span>
                        <span class="post-tag">{back.vol}</span>
                        <span class="post-title">{back.card.title}</span>
                        <span class="post-desc">{back.card.description}</span>
                        <span class="post-meta">
                          <span class="post-date">{formatIssueDate(back.date)}</span>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </main>

          <WebzineFooter />
        </div>
      </div>

      {issue.interview && (
        <InterviewPopup
          interview={issue.interview}
          open={interviewOpen}
          onClose={() => setInterviewOpen(false)}
        />
      )}

      <TabBar path={tabPath} />
    </div>
  );
};
